<template>
    <div class="automation-block-editor send-json-response">
        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">{{ $t('auto.sendJsonResponseResponseMode') }}</div>
            <div class="btnGroup">
                <SupsisButton
                    v-for="type in ['json', 'raw']"
                    :classNames="clone.mode === type ? 'btnGroupItem selected' : 'btnGroupItem '"
                    :primary="clone.mode === type"
                    :ghost="clone.mode !== type"
                    @click="() => changeMode(type)"
                    small
                    :key="type"
                >
                    {{ type }}
                </SupsisButton>
            </div>
        </div>
        <div v-if="clone.mode === 'raw'" class="editor-row customize-editor-minimize">
            <div class="editor-row-title">{{ $t('auto.sendJsonResponseRawContentType') }}</div>
            <CustomPicker
                title="Select Content Type"
                icon="receipt_long"
                :items="rawContentTypeOptions"
                idKey="id"
                labelKey="label"
                v-model="clone.rawContentType"
            />
        </div>

        <div v-if="clone.mode === 'raw'" class="editor-row customize-editor-minimize">
            <div class="editor-row-title">{{ $t('auto.sendJsonResponseRawResponse') }}</div>
            <div id="monaco-raw-editor-container" class="monaco-raw-editor-container"></div>
        </div>

        <div v-if="clone.mode === 'json'" class="editor-row customize-editor-minimize data-items">
            <div class="editor-row-title">{{ $t('auto.sendJsonResponseResponseData') }}</div>
            <DataItem
                v-for="(item, index) in clone.data || []"
                v-model="clone.data[index]"
                :key="index"
                @delete="() => deleteDataItem(index)"
            />
            <SupsisButton small ghost @click="addDataItem"> {{ $t('auto.sendJsonResponseAddDataField') }}</SupsisButton>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import _ from "lodash";
import * as monaco from "monaco-editor";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import StringUtils from "../../../../../../../utils/StringUtils";
import DataItem from "./DataItem.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";

const log = log2("SendJsonResponse");
export default {
    name: "SendJsonResponse",
    components: { DataItem, SupsisButton, CustomPicker },
    props: ["value"],
    data() {
        const clone = _.cloneDeep(this.value) || {
            mode: "json", // json, raw
            rawContentType: "application/json", //application/json,text/plain,application/xml,text/xml
            raw: "",
            data: [
                {
                    key: null,
                    value: null,
                },
            ],
            hashCode: StringUtils.hashCodeObj(clone),
            editor: null,
        };
        return {
            rawContentTypeOptions: [
                { id: "application/json", label: "application/json" },
                { id: "text/plain", label: "text/plain" },
                { id: "application/xml", label: "application/xml" },
                { id: "text/xml", label: "text/xml" },
                { id: "text/html", label: "HTML" },
                { id: "application/javascript", label: "JavaScript" },
                { id: "application/x-javascript", label: "JavaScript" },
            ],
            clone,
            hashCode: StringUtils.hashCodeObj(clone),
        };
    },
    methods: {
        changeMode(mode) {
            console.log("changeMode", mode);
            this.destroyMonaco();
            this.clone.mode = mode;
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick(() => {
                this.initMonaco();
            });
        },
        addDataItem() {
            if (!this.clone.data) {
                this.clone.data = [];
            }
            this.clone.data.push({ key: null, value: null });
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick(() => {});
        },
        deleteDataItem(index) {
            this.clone.data.splice(index, 1);
            this.clone = _.cloneDeep(this.clone);
            this.$nextTick(() => {});
        },
        initMonaco() {
            if (this.clone.mode === "raw") {
                this.$nextTick(() => {
                    const container = document.getElementById("monaco-raw-editor-container");
                    if (container && !this.editor) {
                        this.editor = monaco.editor.create(container, {
                            value: this.clone.raw || "",
                            language: this.getMonacoLanguage(this.clone.rawContentType),
                            theme: "vs-dark",
                            minimap: {
                                enabled: false,
                            },
                            automaticLayout: true,
                            scrollBeyondLastLine: false,
                            lineNumbers: "on",
                            roundedSelection: true,
                            cursorStyle: "line",
                            wordWrap: "on",
                            tabSize: 2,
                            fontSize: 14,
                        });

                        this.editor.onDidChangeModelContent(() => {
                            this.clone.raw = this.editor.getValue();
                        });
                    }
                });
            }
        },
        destroyMonaco() {
            if (this.editor) {
                this.editor.dispose();
                this.editor = null;
            }
        },
        getMonacoLanguage(contentType) {
            switch (contentType) {
                case "application/json":
                    return "json";
                case "application/xml":
                case "text/xml":
                    return "xml";
                case "text/html":
                    return "html";
                case "application/javascript":
                case "application/x-javascript":
                    return "javascript";
                case "text/plain":
                default:
                    return "plaintext";
            }
        },
    },
    mounted() {
        this.initMonaco();
    },
    beforeDestroy() {
        this.destroyMonaco();
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.hashCode = StringUtils.hashCodeObj(newValue);
                this.$emit("input", newValue);
            },
        },
        "clone.rawContentType": {
            handler() {
                if (this.editor && this.clone.mode === "raw") {
                    const newLanguage = this.getMonacoLanguage(this.clone.rawContentType);
                    monaco.editor.setModelLanguage(this.editor.getModel(), newLanguage);
                }
            },
        },
    },
};
</script>

<style lang="scss">
.automation-block-editor.send-json-response {
    padding: 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;

    .editor-row {
        width: 100%;

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
        }
    }

    .btnGroup {
        display: flex;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
        background: var(--color-bg-neutral-default);
        margin: 2px 10px;
        padding: 2px;

        .btnGrupItem {
            &.selected {
                background: var(--color-bg-oncomponent-default);
                box-shadow: var(--shadow-interaction-200);
            }
        }

        .supsis-button {
            border: none !important;
            font-size: 13px;
            height: 25px;
        }
    }

    .data-items {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .webhook-data-item {
            border-bottom: 1px solid var(--color-border-default);
            padding-bottom: 3px;
        }
    }

    .monaco-raw-editor-container {
        width: 100%;
        height: 300px;
        border-radius: 5px;
        overflow: hidden;
        margin-top: 10px;
        border: 1px solid var(--color-border-default);
    }
}
</style>
